import React, { useState, useMemo } from 'react';
import { sanitizeRichHTML } from '../../utils/sanitize';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import GroupWorkIcon from '@mui/icons-material/GroupWork';

const DynamicTemplateResults = ({ 
  data = [], 
  template = {}, 
  parameters = {}, 
  dynamicMetadata = {},
  executionMetadata = {},
  html = null,
  template_type = 'single_query'
}) => {
  // All hooks MUST be called at the top level FIRST - before any conditional returns
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !data || data.length === 0) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [data, sortConfig]);

  // Debug logging
  console.log('[DynamicTemplateResults] Rendering decision:', {
    hasHtml: !!html,
    template_type,
    willRenderHtml: html && (template_type === 'multi_query' || template_type === 'multi-query' || template_type === 'single-query' || template_type === 'playbook'),
    hasData: !!data,
    dataLength: data?.length
  });

  // If HTML content is provided, render it directly
  // This applies to:
  // 1. Multi-query templates (always have HTML)
  // 2. Single-query templates with custom HTML templates
  const isMultiQuery = template_type === 'multi-query' || template_type === 'multi_query';
  const isSingleQueryWithHtml = template_type === 'single-query' && html;
  const isPlaybook = template_type === 'playbook';

  if (html && (isMultiQuery || isSingleQueryWithHtml || isPlaybook)) {
    console.log('[DynamicTemplateResults] Rendering custom HTML template, length:', html?.length);
    return (
      <Box sx={{ mt: 2 }}>
        {/* Template Info Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AnalyticsIcon color="primary" />
            <Typography variant="h6">{template.name || 'Template Results'}</Typography>
            {isMultiQuery && <Chip label="Multi-Query" size="small" color="secondary" />}
            {isPlaybook && <Chip label="Playbook" size="small" color="info" />}
            {isSingleQueryWithHtml && <Chip label="Custom Template" size="small" color="primary" />}
          </Box>
          
          {parameters.start_date && parameters.end_date && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {parameters.start_date} to {parameters.end_date}
            </Typography>
          )}
          
          {executionMetadata?.execution_time && (
            <Typography variant="caption" color="text.secondary">
              Executed in {executionMetadata.execution_time}ms
            </Typography>
          )}
        </Box>

        {/* Render HTML content */}
        <Box 
          sx={{ 
            '& table': {
              width: 'auto',
              borderCollapse: 'collapse',
              mb: 3
            },
            '& th, & td': { 
              padding: '8px 12px', 
              textAlign: 'left',
              borderBottom: '1px solid #e0e0e0'
            },
            '& th': { 
              backgroundColor: '#f5f5f5',
              fontWeight: 600,
              fontSize: '0.875rem'
            },
            '& h3': {
              mt: 3,
              mb: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'primary.main'
            },
            '& h4': {
              mt: 2,
              mb: 1,
              fontSize: '1rem',
              fontWeight: 600
            }
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHTML(html) }}
        />
      </Box>
    );
  }

  // Format numbers for display
  const formatValue = (value, columnName) => {
    if (value === null || value === undefined) return '-';

    const lowerName = columnName.toLowerCase();

    // Image URL columns — render as thumbnail
    if (typeof value === 'string' && lowerName.includes('image_url') && value.startsWith('http')) {
      return (
        <img src={value} alt="" style={{ maxWidth: 60, maxHeight: 60, objectFit: 'contain', borderRadius: 4 }} />
      );
    }

    if (typeof value === 'number') {
      // FIRST: Percentage columns (_pct, _percent) — must come before spend/roas
      // because spend_change_pct contains "spend" and roas_change_pct contains "roas"
      if (lowerName.includes('_pct') || lowerName.includes('_percent')) {
        return `${value.toFixed(1)}%`;
      }

      // Percentage formatting for rates
      if (lowerName.includes('rate') ||
          lowerName.includes('ctr') ||
          lowerName.includes('cvr')) {
        return `${value.toFixed(2)}%`;
      }

      // ROAS formatting
      if (lowerName.includes('roas')) {
        return `${value.toFixed(2)}x`;
      }

      // CPA / CPM formatting
      if (lowerName.includes('cpa') || lowerName.includes('cpm') || lowerName.includes('cost_per')) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      }

      // Currency formatting for revenue, spend, cost, etc.
      if (lowerName.includes('revenue') ||
          lowerName.includes('spend') ||
          lowerName.includes('cost') ||
          lowerName.includes('profit') ||
          lowerName.includes('budget')) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      }

      // Large numbers with commas
      if (Math.abs(value) >= 1000) {
        return new Intl.NumberFormat('en-US').format(Math.round(value));
      }

      // Decimal numbers
      return value % 1 === 0 ? value.toString() : value.toFixed(2);
    }

    // String truncation for long values
    if (typeof value === 'string' && value.length > 50) {
      return (
        <Tooltip title={value}>
          <span>{value.substring(0, 50)}...</span>
        </Tooltip>
      );
    }

    return value;
  };

  // Get column header display name
  const getColumnDisplayName = (columnName) => {
    const displayNames = {
      'total_spend': 'Total Spend',
      'total_revenue': 'Total Revenue',
      'nc_revenue': 'NC Revenue',
      'nc_roas': 'NC ROAS',
      'total_impressions': 'Impressions',
      'total_clicks': 'Clicks',
      'event_date': 'Date',
      'campaign_name': 'Campaign',
      'adset_name': 'Ad Set',
      'ad_name': 'Ad',
      'channel': 'Channel',
      'device_type': 'Device',
      'week': 'Week',
      'month': 'Month'
    };
    
    return displayNames[columnName] || 
           columnName.replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
  };


  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      key: column,
      direction: prevConfig.key === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Generate summary statistics (only for single-query templates)
  const generateSummary = () => {
    if (!data || data.length === 0) return null;
    
    const numericColumns = Object.keys(data[0]).filter(key => 
      typeof data[0][key] === 'number' && 
      !key.toLowerCase().includes('date') &&
      !key.toLowerCase().includes('id')
    );
    
    const summaries = numericColumns.slice(0, 4).map(column => {
      const values = data.map(row => row[column]).filter(v => v !== null && v !== undefined);
      const sum = values.reduce((acc, val) => acc + val, 0);
      const avg = values.length > 0 ? sum / values.length : 0;
      const max = Math.max(...values);
      
      return {
        column,
        sum,
        avg,
        max,
        displayName: getColumnDisplayName(column)
      };
    });
    
    return summaries;
  };

  // Only generate summaries for single-query templates with data
  const summaries = (template_type !== 'multi_query' && data && data.length > 0) ? generateSummary() : null;

  // For single-query templates, check if data exists
  if (template_type !== 'multi_query' && (!data || data.length === 0)) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          No data found for the selected parameters. Try adjusting your filters or date range.
        </Typography>
      </Alert>
    );
  }

  const columns = data && data.length > 0 ? Object.keys(data[0]) : [];

  return (
    <Box sx={{ mt: 2 }}>
      {/* Template Info Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AnalyticsIcon color="primary" />
          <Typography variant="h6">{template.name || 'Template Results'}</Typography>
          {executionMetadata?.has_dynamic_grouping && (
            <Chip icon={<GroupWorkIcon />} label="Dynamic Grouping" size="small" color="primary" />
          )}
        </Box>
        
        {dynamicMetadata?.grouping && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Grouped by: <strong>{dynamicMetadata.grouping.selected_label}</strong>
            {parameters.start_date && parameters.end_date && (
              <>
                {' • '}
                {parameters.start_date} to {parameters.end_date}
              </>
            )}
            {data && data.length > 0 && (
              <>
                {' • '}
                {data.length} rows
              </>
            )}
          </Typography>
        )}
        
        {executionMetadata?.execution_time && (
          <Typography variant="caption" color="text.secondary">
            Executed in {executionMetadata.execution_time}ms
          </Typography>
        )}
      </Box>

      {/* Summary Cards */}
      {summaries && summaries.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summaries.map(summary => (
            <Grid item xs={12} sm={6} md={3} key={summary.column}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {summary.displayName}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {formatValue(summary.sum, summary.column)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">
                      Avg: {formatValue(summary.avg, summary.column)}
                    </Typography>
                    <Typography variant="caption">
                      Max: {formatValue(summary.max, summary.column)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Data Table */}
      {columns.length > 0 && sortedData && sortedData.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {columns.map(column => (
                  <TableCell key={column}>
                    <TableSortLabel
                      active={sortConfig.key === column}
                      direction={sortConfig.key === column ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort(column)}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {getColumnDisplayName(column)}
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                  '&:hover': { backgroundColor: '#f0f0f0' }
                }}
              >
                {columns.map(column => {
                  const value = row[column];
                  const isNumeric = typeof value === 'number';
                  
                  return (
                    <TableCell 
                      key={column}
                      align={isNumeric ? 'right' : 'left'}
                      sx={{ 
                        fontWeight: index === 0 || column === columns[0] ? 600 : 400,
                        color: isNumeric && value < 0 ? 'error.main' : 'inherit'
                      }}
                    >
                      {formatValue(value, column)}
                    </TableCell>
                  );
                })}
              </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Footer Info */}
      {data && data.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {data.length} results
            {dynamicMetadata?.grouping?.available_options && (
              <>
                {' • '}
                Available groupings: {dynamicMetadata.grouping.available_options.map(opt => opt.label).join(', ')}
              </>
            )}
          </Typography>
          
          {executionMetadata?.template_id && (
            <Chip 
              label={`Template: ${executionMetadata.template_id}`} 
              size="small" 
              variant="outlined" 
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default DynamicTemplateResults;
